import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {Router} from "@angular/router";
import {Subscription} from "rxjs";
import {ILaunchpadConfig} from "../../interface/launchpad-config.interface";
import {IQuickAction} from "../../interface/quick-action.interface";
import {ILaunchpadSection} from "../../interface/launchpad-section.interface";
import {LaunchpadRegistryService} from "../../service/launchpad-registry.service";
import {AiChatStateService} from "../../service/ai-chat-state.service";
import {HelpArticleService} from "../../service/api/help-article.service";
import {HelpTourService} from "../../service/api/help-tour.service";
import {HelpArticleDto} from "../../model/dto/help-article.dto";
import {HelpTourDto} from "../../model/dto/help-tour.dto";

/**
 * Abstract base component for module launchpads.
 * Provides common logic for rendering sections, quick actions,
 * loading articles, and triggering guided tours.
 *
 * Module-specific launchpads extend this and provide their config.
 */
@Component({
    selector: 'mvs-launchpad-base',
    templateUrl: './launchpad-base.component.html',
    styleUrls: ['./launchpad-base.component.scss'],
    standalone: false
})
export class LaunchpadBaseComponent implements OnInit, OnChanges, OnDestroy {

    @Input() moduleAlias: string;

    initialized = false;
    config: ILaunchpadConfig;
    sections: ILaunchpadSection[] = [];
    quickActions: IQuickAction[] = [];
    popularArticles: HelpArticleDto[] = [];
    recentArticles: HelpArticleDto[] = [];
    availableTours: HelpTourDto[] = [];
    isLoading = true;

    private subscriptions: Subscription[] = [];

    constructor(
        protected router: Router,
        protected registryService: LaunchpadRegistryService,
        protected chatStateService: AiChatStateService,
        protected articleService: HelpArticleService,
        protected tourService: HelpTourService
    ) {}

    ngOnInit(): void {
        this.initComponent();
    }

    /**
     * Initialize Component.
     */
    initComponent(): void {
        if (this.moduleAlias) {
            this.loadConfig();
        }
        this.initialized = true;
    }

    /**
     * Process changes within Binding.
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }

        if (changes['moduleAlias']) {
            this.initComponent();
        }
    }

    /**
     * Destroy component.
     */
    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    protected loadConfig(): void {
        this.config = this.registryService.getConfig(this.moduleAlias);
        if (!this.config) {
            this.isLoading = false;
            return;
        }

        this.sections = [...this.config.sections].sort((a, b) => a.sortOrder - b.sortOrder);
        this.quickActions = this.config.quickActions || [];

        this.loadArticles();
        this.loadTours();
    }

    protected loadArticles(): void {
        const sub1 = this.articleService.getPopularArticles(this.moduleAlias)
            .subscribe({
                next: articles => this.popularArticles = articles,
                error: err => console.error('Failed to load popular articles', err)
            });
        this.subscriptions.push(sub1);

        const sub2 = this.articleService.getRecentArticles(this.moduleAlias)
            .subscribe({
                next: articles => {
                    this.recentArticles = articles;
                    this.isLoading = false;
                },
                error: err => {
                    console.error('Failed to load recent articles', err);
                    this.isLoading = false;
                }
            });
        this.subscriptions.push(sub2);
    }

    protected loadTours(): void {
        const sub = this.tourService.getUncompletedTours(this.moduleAlias)
            .subscribe({
                next: tours => this.availableTours = tours,
                error: err => console.error('Failed to load tours', err)
            });
        this.subscriptions.push(sub);
    }

    onQuickActionClick(action: IQuickAction): void {
        if (action.route) {
            this.router.navigate([action.route]);
        }
    }

    onOpenAiChat(): void {
        this.chatStateService.setCurrentModule(this.moduleAlias);
        this.chatStateService.openPanel();
    }

    onArticleClick(article: HelpArticleDto): void {
        this.router.navigate(['/lp/article', article.slug]);
    }

    onStartTour(tour: HelpTourDto): void {
        // Tour start is handled by the guided-tour component
        // This emits the event for parent handling
    }
}
